import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { DisabledReasonTooltip } from '@/components/DisabledReasonTooltip/DisabledReasonTooltip'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { getSalesforceAccountLink } from '@/features/HivesOverview/components/ExternalLinks'
import { useOrgSalesforceLink } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import type { SalesforceLinkActionId } from '@/features/SalesforceAccounts/components/SalesforceLinkActionDialog'
import { SalesforceLinkActionDialog } from '@/features/SalesforceAccounts/components/SalesforceLinkActionDialog'
import { useCanManageSalesforceLink } from '@/features/SalesforceAccounts/hooks/useCanManageSalesforceLink'
import { parseLinkResult } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { salesforceAccountDetailRoute } from '@/features/SalesforceAccounts/pages/SalesforceAccountDetailPage'
import {
  Alert,
  Box,
  Button,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type SalesforceLinkAction = {
  id: SalesforceLinkActionId
  label: string
  /** API-correctness: would this call be allowed at all? */
  enabled: boolean
  /** UX-correctness: does this action belong on screen right now? */
  visible: boolean
  actionDisabledReason?: string
}

type ActionButtonProps = {
  action: SalesforceLinkAction
  onAction: (id: SalesforceLinkActionId) => void
}

function SalesforceActionButton({
  action,
  onAction,
}: Readonly<ActionButtonProps>) {
  return (
    <DisabledReasonTooltip
      reason={action.enabled ? undefined : action.actionDisabledReason}
    >
      <Button
        size="xs"
        variant={action.id === 'clear' ? 'outline' : 'solid'}
        colorPalette={action.id === 'clear' ? 'red' : 'brand'}
        disabled={!action.enabled}
        onClick={() => onAction(action.id)}
      >
        {action.label}
      </Button>
    </DisabledReasonTooltip>
  )
}

/**
 * What the card knows about the link right now.
 *
 * `isLinked` and `isUnlinked` are **both false** until the read has actually
 * succeeded. They are not each other's negation on purpose: "we have not
 * asked yet" and "we asked and there is none" are different answers, and
 * treating the first as the second offers a first link on an organisation
 * that already has one.
 */
type LinkReadState = {
  isLoading: boolean
  readFailed: boolean
  isLinked: boolean
  isUnlinked: boolean
  isServiceDisabled: boolean
  accountId?: string
}

type ActionLabels = Record<SalesforceLinkActionId, string>

/**
 * The three action descriptors. Every write is gated on a *successful* read,
 * never merely on "not failed": while the GET is still in flight nothing is
 * shown, because the token these writes depend on does not exist yet.
 */
function buildActions(
  state: LinkReadState,
  canWrite: boolean,
  labels: ActionLabels,
  unreadableReason: string,
): SalesforceLinkAction[] {
  const isReadable = !state.isLoading && !state.readFailed
  const disabledReason = state.readFailed ? unreadableReason : undefined
  const shared = {
    enabled: canWrite && isReadable,
    actionDisabledReason: disabledReason,
  }

  return [
    {
      ...shared,
      id: 'link',
      label: labels.link,
      // Visible while known to be unlinked, and on a failed read, where all
      // three show disabled so the operator can see why none can be used.
      visible: canWrite && (state.readFailed || state.isUnlinked),
    },
    {
      ...shared,
      id: 'repoint',
      label: labels.repoint,
      visible: canWrite && (state.readFailed || state.isLinked),
    },
    {
      ...shared,
      id: 'clear',
      label: labels.clear,
      visible: canWrite && (state.readFailed || state.isLinked),
    },
  ]
}

type ReadErrorProps = {
  isServiceDisabled: boolean
  onRetry: () => void
}

/**
 * "We could not ask" — deliberately an alert, so it can never be mistaken for
 * the plain "not linked" sentence. The role is set explicitly: Chakra v3's
 * `Alert.Root` renders a plain div, so without it the distinction is only
 * visual and a screen reader would hear the two identically.
 */
function SalesforceLinkReadError({
  isServiceDisabled,
  onRetry,
}: Readonly<ReadErrorProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <Alert.Root status="warning" role="alert">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>
          {String(t('salesforceLink.card.error.title'))}
        </Alert.Title>
        <Alert.Description>
          {String(
            t(
              isServiceDisabled
                ? 'salesforceLink.card.error.serviceDisabled'
                : 'salesforceLink.card.error.description',
            ),
          )}
        </Alert.Description>
        <Box mt={2}>
          <Button size="xs" variant="outline" onClick={onRetry}>
            {String(t('salesforceLink.card.error.retry'))}
          </Button>
        </Box>
      </Alert.Content>
    </Alert.Root>
  )
}

function SalesforceLinkValue({ accountId }: Readonly<{ accountId: string }>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <VStack align="start" gap={1}>
      <Text fontSize="xs" color="text.text-subtle">
        {String(t('salesforceLink.card.accountIdLabel'))}
      </Text>
      <HStack gap={4} flexWrap="wrap">
        <CopyableValue
          value={accountId}
          externalLink={getSalesforceAccountLink(accountId)}
          fontFamily="mono"
        />
        <Link
          to={salesforceAccountDetailRoute.to}
          params={{ salesforceAccountId: accountId }}
        >
          {String(t('salesforceLink.card.viewOrgsOnAccount'))}
        </Link>
      </HStack>
    </VStack>
  )
}

type Props = {
  orgId: string
}

/**
 * The organisation's Salesforce account link, on `/orgs/$orgId`.
 *
 * This card is where the compare-and-swap token originates: it owns the only
 * `useGetSalesforceAccount` call in the flow and hands the result down as
 * `currentAccountId`. No form re-reads it, and no operator types it.
 *
 * Actions live *inside* the card, not in the page header: they edit the data
 * this section owns, which makes them section-level.
 *
 * Read before write, always. A failed read disables all three writes and a
 * read still in flight offers none of them: offering a write on either binds
 * an org to the wrong commercial account.
 */
export function SalesforceAccountCard({ orgId }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { canRead, canWrite } = useCanManageSalesforceLink()
  const [action, setAction] = useState<SalesforceLinkActionId | null>(null)

  const query = useOrgSalesforceLink(orgId, { enabled: canRead, retry: false })

  // Viewers without the read role see nothing at all — not an empty card, not
  // a permission notice. `canRead` reads false while the roles query is still
  // in flight and self-corrects on the next render; nothing irreversible
  // happens here, so it needs no `isResolved` gate.
  if (!canRead) {
    return null
  }

  const accountId = query.data?.salesforceAccountId
  const isLinked =
    query.isSuccess && query.data?.linked === true && Boolean(accountId)
  const state: LinkReadState = {
    isLoading: query.isLoading,
    readFailed: query.isError,
    isLinked,
    // Only a read that came back says the organisation has no link.
    isUnlinked: query.isSuccess && !isLinked,
    isServiceDisabled:
      query.isError && parseLinkResult(query.error).status === 503,
    accountId,
  }

  const actions = buildActions(
    state,
    canWrite,
    {
      link: String(t('salesforceLink.card.actions.link.label')),
      repoint: String(t('salesforceLink.card.actions.repoint.label')),
      clear: String(t('salesforceLink.card.actions.clear.label')),
    },
    String(t('salesforceLink.card.disabledReason.linkUnknown')),
  )

  const refetch = () => {
    void query.refetch()
  }

  /**
   * The form's re-read. Reports whether the read actually succeeded:
   * `refetch()` resolves on failure too, so its mere resolution says nothing.
   */
  const handleReRead = async () => {
    const result = await query.refetch()
    return result.isSuccess === true
  }

  return (
    <>
      <SectionCard
        title={String(t('salesforceLink.card.title'))}
        actions={actions
          .filter((candidate) => candidate.visible)
          .map((candidate) => (
            <SalesforceActionButton
              key={candidate.id}
              action={candidate}
              onAction={setAction}
            />
          ))}
      >
        <VStack align="stretch" gap={3}>
          {state.isLoading ? <Skeleton h={10} /> : null}

          {state.readFailed ? (
            <SalesforceLinkReadError
              isServiceDisabled={state.isServiceDisabled}
              onRetry={refetch}
            />
          ) : null}

          {isLinked && accountId ? (
            <SalesforceLinkValue accountId={accountId} />
          ) : null}

          {state.isUnlinked ? (
            <Text fontSize="sm" color="text.text-subtle">
              {String(t('salesforceLink.card.notLinked'))}
            </Text>
          ) : null}
        </VStack>
      </SectionCard>

      <SalesforceLinkActionDialog
        action={action}
        orgId={orgId}
        currentAccountId={accountId}
        onClose={() => setAction(null)}
        onReRead={handleReRead}
      />
    </>
  )
}
